import {FC, useState} from 'react';
import {Autocomplete, Box, Button, Pagination, TextField} from '@mui/material';
import {useSnackbar} from 'notistack';

import {
  useBindExternalSubCategories,
  useGetUnmappedExternalSubCategories,
} from '../../apiHooks';
import {ExternalSubCategory} from '../../types';

interface Props {
  categorySlug: string;
  subCategoryId: string;
}

export const ExternalSubCategoryBindControl: FC<Props> = ({
  categorySlug,
  subCategoryId,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ExternalSubCategory[]>([]);
  const {data: unmapped, isPending: isUnmappedPending} =
    useGetUnmappedExternalSubCategories({
      categorySlug,
      page,
      pageSize: 100,
    });
  const {mutateAsync: bind, isPending: isBinding} =
    useBindExternalSubCategories();

  const handleBind = async () => {
    if (selected.length === 0) return;

    try {
      await bind({
        subCategoryId,
        externalSubCategoryIds: selected.map(item => item.id),
      });
      setSelected([]);
      setPage(1);
      enqueueSnackbar('External subcategories bound successfully', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to bind external subcategories', {
        variant: 'error',
      });
    }
  };

  return (
    <Box display="flex" alignItems="flex-start" gap={1} width={620}>
      <Box flex={1}>
        <Autocomplete
          multiple
          limitTags={1}
          size="small"
          options={unmapped?.items ?? []}
          value={selected}
          loading={isUnmappedPending}
          getOptionLabel={option => `${option.name} (${option.externalId})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setSelected(value)}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Select external subcategories"
              sx={{
                '& .MuiInputBase-root': {
                  height: 43,
                },
              }}
            />
          )}
        />

        {(unmapped?.totalPages ?? 0) > 1 && (
          <Pagination
            count={unmapped?.totalPages}
            page={page}
            size="small"
            onChange={(_, nextPage) => setPage(nextPage)}
            sx={{mt: 1}}
          />
        )}
      </Box>

      <Button
        onClick={() => void handleBind()}
        disabled={isBinding || selected.length === 0}
      >
        Bind
      </Button>
    </Box>
  );
};
